import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Layout from "../../components/Layout";
import vendorAPI from "../../api/vendorAPI";
import VendorForm from "../../components/VendorForm";
import { useVendorTerms } from "../../utils/terminology";
import AuditLogTab from "../../components/AuditLogTab";

const EditVendorScreen = () => {
  const term = useVendorTerms();
  const navigate = useNavigate();
  const { vendorId } = useParams();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [vendor, setVendor] = useState(null);
  const [activeTab, setActiveTab] = useState("details");

  useEffect(() => {
    fetchVendor();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorId]);

  const fetchVendor = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await vendorAPI.getVendor(vendorId);
      
      // Handle different response structures
      const vendorData = response?.data?.data || response?.data || response;
      setVendor(vendorData);
    } catch (err) {
      console.error("Error fetching vendor:", err);
      setError(err.message || `Failed to load ${term.lower}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (payload) => {
    try {
      setSaving(true);
      setError("");
      setSuccess("");

      const response = await vendorAPI.updateVendor(vendorId, payload);
      const updated = response?.data?.data || response?.data || response;

      setVendor(updated);
      setSuccess(`${term.one} updated successfully`);
      
      // Scroll to top to show success message
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      console.error("Error updating vendor:", err);
      const errorMsg = err?.backend?.message || err?.response?.data?.message || err?.message || "Failed to update vendor";
      setError(errorMsg);
      throw err; // Re-throw so VendorForm can handle it
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate("/client/vendors");
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "active":
        return <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">Active</span>;
      case "inactive":
        return <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800">Inactive</span>;
      case "suspended":
        return <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-800">Suspended</span>;
      default:
        return <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800">{status}</span>;
    }
  };

  if (loading && !vendor) {
    return (
      <Layout pageTitle={`Edit ${term.one}`}>
        <div className="max-w-6xl mx-auto px-6 py-6 bg-gray-50 min-h-screen">
          <div className="bg-white rounded-lg shadow-md p-6 text-center">
            <div className="text-lg">Loading {term.lower}...</div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!vendor && !loading) {
    return (
      <Layout pageTitle={`Edit ${term.one}`}>
        <div className="max-w-6xl mx-auto px-6 py-6 bg-gray-50 min-h-screen">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            Vendor not found or you do not have permission to view it.
          </div>
          <button
            onClick={() => navigate("/client/vendors")}
            className="px-4 py-2 bg-teal-500 text-white rounded hover:bg-teal-600"
          >
            Back to Vendors
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout pageTitle={`Edit ${term.one}`}>
      <div className="max-w-6xl mx-auto px-6 py-6 bg-gray-50 min-h-screen">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">
            <button
              type="button"
              onClick={() => navigate("/client/vendors")}
              className="text-teal-600 hover:underline"
            >
              Vendors
            </button>{" "}
            / Edit
          </h1>
        </div>

        {/* Vendor header row */}
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between mb-4">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-semibold text-gray-800">{vendor?.name}</h2>
            {vendor?.status && getStatusBadge(vendor.status)}
          </div>
          {vendor?.code && (
            <span className="font-mono text-sm text-gray-600">Code: {vendor.code}</span>
          )}
        </div>

        {/* Error/Success notifications */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            <div className="flex justify-between items-start">
              <div>
                <strong className="font-bold">Error: </strong>
                <span className="block sm:inline">{error}</span>
              </div>
              <button onClick={() => setError("")} className="ml-4 text-red-900 hover:text-red-600 text-lg">
                ×
              </button>
            </div>
          </div>
        )}

        {success && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
            <div className="flex justify-between items-start">
              <div>
                <strong className="font-bold">Success: </strong>
                <span className="block sm:inline">{success}</span>
              </div>
              <button onClick={() => setSuccess("")} className="ml-4 text-green-900 hover:text-green-600 text-lg">
                ×
              </button>
            </div>
          </div>
        )}

        {/* Tab navigation */}
        <div className="flex gap-8 border-b mb-4 overflow-x-auto whitespace-nowrap">
          <button
            className={`pb-2 ${
              activeTab === "details" ? "border-b-2 border-teal-600 font-semibold" : "text-gray-600"
            }`}
            onClick={() => setActiveTab("details")}
            type="button"
          >
            DETAILS
          </button>

          <button
            className={`pb-2 ${
              activeTab === "audit" ? "border-b-2 border-teal-600 font-semibold" : "text-gray-600"
            }`}
            onClick={() => setActiveTab("audit")}
            type="button"
          >
            AUDIT LOG
          </button>
        </div>

        {/* Tab content */}
        <div className="bg-white rounded-lg shadow-md">
          {activeTab === "details" && (
            <div className="p-6">
              <h3 className="text-lg font-semibold mb-4">{term.one} Information</h3>
              <VendorForm
                initialVendor={vendor}
                onSave={handleSave}
                onCancel={handleCancel}
                saving={saving}
                showButtons={true}
              />
            </div>
          )}

          {activeTab === "audit" && (
            <div className="p-6">
              <h3 className="text-lg font-semibold mb-4">Change History</h3>
              <AuditLogTab 
                entityType="Vendor" 
                entityId={vendorId}
                apiEndpoint={`/vendors/${vendorId}/audit-trail`}
              />
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default EditVendorScreen;
